import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Coins, Send, History as HistoryIcon, Package, Store } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { agentApi } from "@/lib/extensions-api";
import { formatNumber, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatusPill } from "@/components/ui/StatusPill";
import { EmptyState } from "@/components/ui/EmptyState";
import { CopyableId } from "@/components/ui/CopyableId";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

export function AgentDashboardPage() {
  const qc = useQueryClient();
  const profile = useQuery({ queryKey: ["agent-profile"], queryFn: agentApi.profile });
  const history = useQuery({ queryKey: ["agent-history"], queryFn: () => agentApi.history() });

  const [shopName, setShopName] = useState(profile.data?.shop_name || "");
  const [shopDesc, setShopDesc] = useState(profile.data?.shop_description || "");

  const updateShop = useMutation({
    mutationFn: () => agentApi.updateShop({ shop_name: shopName, shop_description: shopDesc }),
    onSuccess: () => {
      toast.success("Shop information updated!");
      qc.invalidateQueries({ queryKey: ["agent-profile"] });
    }
  });

  const recent = (history.data ?? []).slice(0, 3);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Agent & Vendor Portal</h1>
        <p className="text-sm text-surface-500 mt-1">Manage your point allocations and your vendor shop.</p>
      </header>

      {/* Point Stats */}
      {profile.isLoading ? (
        <Skeleton className="h-40 rounded-xl" />
      ) : profile.data && (
        <Card className="bg-gradient-to-br from-warning-500 via-warning-600 to-warning-700 text-white border-0 overflow-hidden relative">
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
          <CardBody className="relative">
            <div className="text-xs uppercase tracking-wide text-warning-100">Available Point Allocation</div>
            <div className="mt-2 flex items-baseline gap-3">
              <div className="text-5xl font-bold font-mono">{formatNumber(profile.data.available_points)}</div>
              <div className="text-sm text-warning-100">points</div>
            </div>
            <div className="mt-4 flex items-center gap-6 text-sm text-warning-100">
              <div>Loaded: <span className="font-bold text-white">{formatNumber(profile.data.total_loaded)}</span></div>
              <div>Distributed: <span className="font-bold text-white">{formatNumber(profile.data.total_distributed)}</span></div>
              <div className="ml-auto"><StatusPill status={profile.data.status} /></div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Grid: Shop Settings & Quick Links */}
      <div className="grid gap-6 lg:grid-cols-2">
        
        {/* Vendor Settings */}
        <Card>
          <CardHeader>
            <div className="flex gap-2 items-center">
              <CardTitle><Store className="w-5 h-5 text-brand-600"/> Shop Settings</CardTitle>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <Input 
              label="Shop / Vendor Name" 
              placeholder="E.g. XYZ Electronics" 
              defaultValue={profile.data?.shop_name}
              onChange={(e) => setShopName(e.target.value)} 
            />
            <div>
              <label className="block text-sm font-medium text-surface-800 mb-1.5">Shop Description</label>
              <textarea 
                className="w-full h-20 px-3.5 py-2.5 bg-white border border-surface-300 rounded-lg text-sm resize-none focus:border-brand-500" 
                defaultValue={profile.data?.shop_description}
                onChange={(e) => setShopDesc(e.target.value)}
              />
            </div>
            <Button onClick={() => updateShop.mutate()} loading={updateShop.isPending}>Save Shop Info</Button>
          </CardBody>
        </Card>

        {/* Quick Links */}
        <div className="space-y-4">
          <Link to="/agent/orders" className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-surface-200 shadow-sm hover:shadow-md transition-all group">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 grid place-items-center group-hover:scale-110 transition-transform"><Package className="h-6 w-6"/></div>
            <div>
              <div className="font-bold text-surface-900 text-lg">Manage Orders</div>
              <div className="text-xs text-surface-500">View orders placed for your shop products</div>
            </div>
          </Link>
          
          <Link to="/agent/distribute" className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-surface-200 shadow-sm hover:shadow-md transition-all group">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center group-hover:scale-110 transition-transform"><Send className="h-6 w-6"/></div>
            <div>
              <div className="font-bold text-surface-900 text-lg">Distribute Points</div>
              <div className="text-xs text-surface-500">Send points to customers manually</div>
            </div>
          </Link>
        </div>
      </div>

    </div>
  );
}